import getRouterBasename from '@/lib/router';
import {
    isEmbeddedSidebar,
    embeddedReportToolsEnabled,
    embeddedMcpEnabled,
    embeddedMcpServers,
    embeddedModel
} from '@/lib/embedded-sidebar';
// Side-effect import: installs the parent-origin capture listener at app start,
// before we post requestAccessToken to the host, so it catches the host's
// reply messages and records the embedding origin for thread-share links.
import '@/lib/parent-origin';
import App from 'App';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AccessDenied from '@/pages/AccessDenied';
import Organizations from '@/pages/Organizations';

import {
    useApi,
    useAuth,
    useChatInteract,
    useConfig,
    sessionState
} from '@chainlit/react-client';

import { ThemeProvider } from '@/components/ThemeProvider';

import { useOrganizationFont } from '@/hooks/useOrganizationFont';
import { useRecoilValue } from 'recoil';

export default function AppWrapper() {
    const [tokenCheckComplete, setTokenCheckComplete] = useState(false);

    // Listen for access token from parent window
    useEffect(() => {
        // Skip authentication if we're on the access-denied page to avoid redirect loop
        const isAccessDeniedPage =
            window.location.pathname === getRouterBasename() + '/access-denied';

        if (isAccessDeniedPage) {
            setTokenCheckComplete(true);
            return;
        }

        let hasReceivedToken = false;
        let isAuthenticating = false;
        let receivedAccessToken: string | null = null;
        let receivedRefreshToken: string | null = null;

        // Check if we're in an iframe
        const isInIframe =
            typeof window !== 'undefined' && window.parent !== window;

        if (!isInIframe) {
            // Not in iframe, proceed with normal flow immediately
            console.log('Not in iframe, proceeding with normal authentication');
            setTokenCheckComplete(true);
            return;
        }

        // In iframe - always wait for token from parent (even if we have a cookie)
        // This allows parent to send updated tokens
        console.log('In iframe, waiting for tokens from parent...');

        // Set a timeout to stop waiting for tokens (3s for embedded, 10s otherwise)
        const tokenTimeoutMs = isEmbeddedSidebar ? 3000 : 10000;
        const timeout = setTimeout(() => {
            if (!hasReceivedToken && !isAuthenticating) {
                console.error(
                    'Token request timeout - both access and refresh tokens are required'
                );
                setTokenCheckComplete(true);
            }
        }, tokenTimeoutMs);

        let authenticationTimer: NodeJS.Timeout | null = null;

        const authenticate = async () => {
            if (
                isAuthenticating ||
                !receivedAccessToken ||
                !receivedRefreshToken
            )
                return;

            isAuthenticating = true;
            clearTimeout(timeout);
            if (authenticationTimer) clearTimeout(authenticationTimer);

            try {
                // Remove the event listener
                window.removeEventListener('message', handleMessage);

                // Send tokens in body to avoid 431 with large Keycloak JWTs
                const requestBody = JSON.stringify({
                    access_token: receivedAccessToken,
                    refresh_token: receivedRefreshToken
                });

                // Authenticate using the Keycloak token via the API endpoint
                const response = await fetch('/auth/keycloak', {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json'
                    },
                    credentials: 'include',
                    body: requestBody
                });

                if (!response.ok) {
                    const errorText = await response.text();
                    console.error('Keycloak authentication failed:', errorText);

                    // Handle 403 Forbidden - user doesn't have required role
                    if (response.status === 403) {
                        console.log(
                            'Access denied - redirecting to access-denied page'
                        );
                        window.location.href =
                            getRouterBasename() + '/access-denied';
                        return;
                    }

                    isAuthenticating = false;
                    setTokenCheckComplete(true);
                    return;
                }

                console.log('Keycloak authentication successful');

                // Set tokenCheckComplete to continue rendering
                // The cookie has been set by the backend
                setTokenCheckComplete(true);
            } catch (error) {
                console.error('Error processing token:', error);
                isAuthenticating = false;
                setTokenCheckComplete(true);
            }
        };

        const handleMessage = async (event: MessageEvent) => {
            // Handle access token
            if (event.data && event.data.accessToken && !receivedAccessToken) {
                receivedAccessToken = event.data.accessToken;
                console.log('Access token received from parent window');
            }

            // Handle refresh token
            if (
                event.data &&
                event.data.refreshToken &&
                !receivedRefreshToken
            ) {
                receivedRefreshToken = event.data.refreshToken;
                console.log('Refresh token received from parent window');
            }

            // Once we have both tokens, authenticate
            if (
                receivedAccessToken &&
                receivedRefreshToken &&
                !hasReceivedToken &&
                !isAuthenticating
            ) {
                hasReceivedToken = true;
                clearTimeout(timeout);
                if (authenticationTimer) clearTimeout(authenticationTimer);
                authenticate();
            }
        };

        window.addEventListener('message', handleMessage);

        // Request access token and refresh token from parent window
        window.parent.postMessage('requestAccessToken', '*');
        window.parent.postMessage('requestRefreshToken', '*');

        return () => {
            clearTimeout(timeout);
            if (authenticationTimer) clearTimeout(authenticationTimer);
            window.removeEventListener('message', handleMessage);
        };
    }, []);

    // Check if we're on the organizations page FIRST before any hooks
    const isOrganizationsPage =
        window.location.pathname === getRouterBasename() + '/organizations';

    // Check if we're on the access-denied page
    const isAccessDeniedPage =
        window.location.pathname === getRouterBasename() + '/access-denied';

    // For organizations page, render directly without router or auth
    if (isOrganizationsPage) {
        return (
            <ThemeProvider>
                <Organizations />
            </ThemeProvider>
        );
    }

    // For access-denied page, render directly without auth check
    if (isAccessDeniedPage) {
        return (
            <ThemeProvider>
                <AccessDenied />
            </ThemeProvider>
        );
    }

    // Don't render anything until token check is complete
    if (!tokenCheckComplete) {
        return null;
    }

    return <AuthenticatedApp />;
}

// Separate component for authenticated app
function AuthenticatedApp() {
    const [translationLoaded, setTranslationLoaded] = useState(false);
    const { isAuthenticated, isReady, user } = useAuth();
    const { language: languageInUse } = useConfig();
    const { i18n } = useTranslation();
    const { windowMessage } = useChatInteract();
    const session = useRecoilValue(sessionState);
    const socketConnected = session?.socket?.connected ?? false;
    const messageBufferRef = useRef<any[]>([]);

    function handleChangeLanguage(languageBundle: any): void {
        i18n.addResourceBundle(languageInUse, 'translation', languageBundle);
        i18n.changeLanguage(languageInUse);
    }

    const { data: translations } = useApi<any>(
        `/project/translations?language=${languageInUse}`
    );

    useEffect(() => {
        if (!translations) return;
        handleChangeLanguage(translations.translation);
        setTranslationLoaded(true);
    }, [translations]);

    // Replay buffered messages when socket connects
    useEffect(() => {
        if (socketConnected && messageBufferRef.current.length > 0) {
            for (const data of messageBufferRef.current) {
                windowMessage(data);
            }
            messageBufferRef.current = [];
        }
    }, [socketConnected, windowMessage]);

    useEffect(() => {
        const handleWindowMessage = (event: MessageEvent) => {
            if (session?.socket?.connected) {
                windowMessage(event.data);
            } else {
                messageBufferRef.current.push(event.data);
            }
        };
        window.addEventListener('message', handleWindowMessage);
        return () => window.removeEventListener('message', handleWindowMessage);
    }, [windowMessage, session?.socket?.connected]);

    // Signal parent that iframe is ready to receive messages (e.g. report context)
    // Only fire after socket is connected so windowMessage can actually deliver messages
    useEffect(() => {
        if (isEmbeddedSidebar && isReady && isAuthenticated && socketConnected) {
            window.parent.postMessage('chatReady', '*');
            // Tell the backend this chat is embedded inside an AI-enabled report so
            // it loads the Power BI report tools + prompt guidance. The standalone
            // chat never sends this, keeping report capabilities out of its context.
            // `reportToolsEnabled` carries the report author's per-report choice
            // (from the host's ?report-tools param): when false the backend runs the
            // chat in "plain chat" mode — no report tools, behaving like the AI tab.
            // `mcpServers` are the MCP servers the report author attached to the
            // report (via ?mcp-servers=<a,b,c>); empty means no MCP. The backend
            // resolves them against the org's MCP catalog — the user's own MCP
            // selection is never used in an embedded chat.
            windowMessage({
                type: 'report_embed_context',
                embedded: true,
                reportToolsEnabled: embeddedReportToolsEnabled,
                mcpToolsEnabled: embeddedMcpEnabled,
                mcpServers: embeddedMcpServers,
                model: embeddedModel
            });
        }
    }, [isReady, isAuthenticated, socketConnected, windowMessage]);

    // Check if user needs organization - backend provides this via user metadata
    useEffect(() => {
        if (isReady && isAuthenticated && user) {
            const userMetadata = user as any;
            const hasOrganization = userMetadata.metadata?.organization;

            if (!hasOrganization) {
                // User is authenticated but has no organization selected
                window.location.href = getRouterBasename() + '/organizations';
            }
        }
    }, [isReady, isAuthenticated, user]);

    useOrganizationFont();

    if (!translationLoaded && !isEmbeddedSidebar) return null;

    // Prevent rendering app if user lacks organization
    if (isReady && isAuthenticated && user) {
        const userMetadata = user as any;
        if (!userMetadata.metadata?.organization) {
            return null; // Will redirect via useEffect above
        }
    }

    if (
        isReady &&
        !isAuthenticated &&
        window.location.pathname !== getRouterBasename() + '/login' &&
        window.location.pathname !== getRouterBasename() + '/login/callback'
    ) {
        // Force redirect to login if not authenticated
        const loginUrl = getRouterBasename() + '/login';
        if (window.location.href !== loginUrl) {
            console.log('Not authenticated, redirecting to login...');
            window.location.href = loginUrl;
        }
    }
    return <App />;
}
